import { RequestStatusEnum, TEAM_ROLES, Timezone } from "@nlc/shared-types";
import { twMerge } from "tailwind-merge";

import type { FeatureKey } from "@/features/auth/permissions/config.ts";

import { Badge } from "@/components/badge";
import { Text } from "@/components/text";
import { FeatureGuard } from "@/features/auth/permissions/feature-guard.tsx";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess.tsx";
import { useDateDisplay } from "@/features/request/edit/hooks/useDateDisplay.tsx";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm.tsx";
import { useGetBrands } from "@/features/request/edit/hooks/useGetBrands.tsx";
import { useTemplateLock } from "@/features/request/edit/hooks/useTemplateLock.tsx";
import { STATUS_OPTIONS } from "@/features/shared/constants/statusColors";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useUser } from "@/hooks/useUser.tsx";
import { dateFormatterEst } from "@/utils/dateFormatterEst.ts";

import type { BadgeOption } from "./SelectWithBadges.tsx";

import { DatePickerPopover } from "./DatePickerPopover.tsx";
import { SelectAssignee } from "./SelectAssignee.tsx";
import { SelectBrand } from "./SelectBrand.tsx";
import { SelectWithBadges } from "./SelectWithBadges.tsx";

const COMPLEXITY_OPTIONS: BadgeOption[] = [
  { id: 1, label: "Basic", variant: "success" },
  { id: 2, label: "Average", variant: "warning" },
  { id: 3, label: "Complex", variant: "pink" },
];

export function InformationBar() {
  const { data: request } = useGetRequest();
  const { data: user } = useUser();
  const { isLocked, isRealTemplate } = useTemplateLock();
  const isTeamUser =
    user?.user?.roleId && TEAM_ROLES.includes(user.user.roleId);
  const timezone = isTeamUser
    ? Timezone.EST
    : (user?.user?.timezone ?? Timezone.EST);

  return (
    <div className="inline-flex justify-start items-center gap-lg text-sm px-3xl pt-lg pb-2xl border-b border-border-primary overflow-x-auto scrollbar-none @container">
      <Information
        divider="trailing"
        features="edit-request.internal-due"
        label="Internal due"
      >
        <DatePickerPopover name="internalDueDate" timezone={timezone} />
      </Information>

      <Information label="Due">
        {isRealTemplate ? (
          <ShowDueDate />
        ) : (
          <FeatureGuard
            fallback={<ShowDueDate />}
            feature="edit-request.due.edit"
          >
            <DatePickerPopover name="dueDate" timezone={Timezone.EST} />
          </FeatureGuard>
        )}
      </Information>

      <Information divider="leading" label="Status">
        {isRealTemplate ? (
          <ShowStatus />
        ) : (
          <FeatureGuard
            fallback={<ShowStatus />}
            feature="edit-request.status.edit"
          >
            {request?.statusId === RequestStatusEnum.Completed ? (
              <ShowStatus />
            ) : (
              <SelectWithBadges
                name="statusId"
                options={STATUS_OPTIONS.filter(
                  (opt) => opt.id !== RequestStatusEnum.Completed,
                )}
              />
            )}
          </FeatureGuard>
        )}
      </Information>

      <Information
        divider="leading"
        features={[
          "edit-request.complexity.edit",
          "edit-request.complexity.view",
        ]}
        label="Complexity"
      >
        {isLocked ? (
          <ShowComplexity />
        ) : (
          <FeatureGuard
            fallback={<ShowComplexity />}
            feature="edit-request.complexity.edit"
          >
            <SelectWithBadges
              name="complexityId"
              options={COMPLEXITY_OPTIONS}
            />
          </FeatureGuard>
        )}
      </Information>

      <Information
        divider="leading"
        features={["edit-request.assignee.edit", "edit-request.assignee.view"]}
        label="Assignee"
      >
        {isLocked ? (
          <ShowAssignee />
        ) : (
          <FeatureGuard
            fallback={<ShowAssignee />}
            feature="edit-request.assignee.edit"
          >
            <SelectAssignee />
          </FeatureGuard>
        )}
      </Information>

      <Information
        divider="leading"
        features={["edit-request.brand.edit", "edit-request.brand.view"]}
        label="Brand"
      >
        {isLocked ? (
          <ShowBrand />
        ) : (
          <FeatureGuard
            fallback={<ShowBrand />}
            feature="edit-request.brand.edit"
          >
            <SelectBrand />
          </FeatureGuard>
        )}
      </Information>
    </div>
  );
}

function Information({
  children,
  className,
  divider,
  features,
  label,
}: {
  children: React.ReactNode;
  className?: string;
  divider?: "leading" | "trailing";
  features?: FeatureKey | FeatureKey[];
  label?: string;
}) {
  const { canAccess } = useRoleAccess();

  if (features) {
    const keys = Array.isArray(features) ? features : [features];

    if (!keys.some((f) => canAccess(f))) return null;
  }

  const dividerComponent = (
    <div className="w-px h-[22px] bg-border-primary shrink-0" />
  );

  const content = (
    <div
      className={twMerge(
        "flex items-center gap-2 flex-row shrink-0",
        className,
      )}
    >
      {label && (
        <Text className="text-text-secondary whitespace-nowrap">{label}</Text>
      )}
      {children}
    </div>
  );

  return (
    <>
      {divider === "leading" && dividerComponent}
      {content}
      {divider === "trailing" && dividerComponent}
    </>
  );
}

function ShowStatus() {
  const { getValues } = useEditRequestFormContext();
  const { isRealTemplate } = useTemplateLock();
  const status = isRealTemplate
    ? RequestStatusEnum.ReadyForReview
    : getValues("statusId");

  return (
    <Badge
      isRounded
      size="small"
      variant={
        STATUS_OPTIONS.find((opt) => opt.id === status)?.variant || "primary"
      }
    >
      {STATUS_OPTIONS.find((opt) => opt.id === status)?.label}
    </Badge>
  );
}

function ShowComplexity() {
  const { getValues } = useEditRequestFormContext();
  const complexity = getValues("complexityId");
  const complexityOption = COMPLEXITY_OPTIONS.find(
    (opt) => opt.id === complexity,
  );

  return (
    <Badge size="small" variant={complexityOption?.variant || "primary"}>
      {complexityOption?.label}
    </Badge>
  );
}
function ShowBrand() {
  const { getValues } = useEditRequestFormContext();
  const { data: brands } = useGetBrands();
  const brand = getValues("brandId");

  return (
    <Badge className="whitespace-nowrap" size="small" variant="primary">
      {brands?.find((opt) => opt.id === brand)?.name || "No brand"}
    </Badge>
  );
}

function ShowDueDate() {
  const { watch } = useEditRequestFormContext();
  const { isRealTemplate } = useTemplateLock();
  const dueDate = isRealTemplate ? null : watch("dueDate");
  const dateDisplay = useDateDisplay(dueDate);

  if (!dueDate) {
    return (
      <Badge
        className="whitespace-nowrap"
        isRounded
        size="small"
        variant="primary"
      >
        No due date
      </Badge>
    );
  }

  if (dateDisplay?.isPill) {
    return (
      <Badge
        className="whitespace-nowrap"
        isRounded
        size="small"
        variant={dateDisplay.badgeVariant}
      >
        {dateDisplay.label}
      </Badge>
    );
  }

  return (
    <span
      className={`text-sm font-medium whitespace-nowrap ${dateDisplay?.colorClass ?? "text-text-tertiary"}`}
    >
      {dateDisplay?.label ?? dateFormatterEst(dueDate.toDate(Timezone.EST))}
    </span>
  );
}

function ShowAssignee() {
  const { watch } = useEditRequestFormContext();

  const assignee = watch("assignee");

  if (!assignee) {
    return (
      <Badge className="whitespace-nowrap" size="small" variant="primary">
        No assignee
      </Badge>
    );
  }

  const { designation, name } = assignee;
  const displayText = designation ? `${name} (${designation})` : name;

  return (
    <Badge className="whitespace-nowrap" size="small" variant="primary">
      {displayText}
    </Badge>
  );
}
